import { useState, useCallback, useMemo, useEffect } from 'react';
import { findValidPlays, suitValues } from '../shared/core/gameLogic';
import soundService from '../soundService';

/**
 * Hook for managing card selection logic in the playing phase.
 * Handles card selection, validation, and sorting.
 *
 * @param {Object} gameState - The current game state
 * @param {number} activePlayerIndex - The currently active player (from useActivePlayer)
 * @param {boolean} isTestMode - Whether we're in test mode
 * @param {boolean} isExchangePhase - Whether we're in exchange phase (disables auto-select)
 * @param {Object} minCard - The minimum card for first turn validation
 */
export function useCardSelection({
  gameState,
  activePlayerIndex,
  isTestMode,
  isExchangePhase = false,
  minCard,
}) {
  const [selectedCards, setSelectedCards] = useState([]);
  const [sortMode, setSortMode] = useState('value'); // 'value' or 'suit'

  // Get current hand based on mode
  const currentHand = useMemo(() => {
    if (isTestMode) {
      // Test mode: use activePlayerIndex to get the correct hand
      if (!gameState?.hands) return [];
      return gameState.hands[activePlayerIndex] || [];
    } else {
      // Multiplayer: use myHand directly (hands array might be empty)
      return gameState?.myHand || gameState?.hands?.[0] || [];
    }
  }, [gameState?.hands, gameState?.myHand, activePlayerIndex, isTestMode]);

  // Calculate valid plays
  const validPlays = useMemo(() => {
    if (!currentHand || currentHand.length === 0) return [];
    return findValidPlays(
      currentHand,
      gameState?.lastPlay,
      [1, 2, 3, 4, 5],
      gameState?.isFirstTurn,
      minCard
    );
  }, [currentHand, gameState?.lastPlay, gameState?.isFirstTurn, minCard]);

  // Has valid plays flag
  const hasValidPlays = validPlays.length > 0;

  // Auto-select single valid play and clear selection on hand change
  // Skip auto-select during exchange phase
  useEffect(() => {
    setSelectedCards([]);
    // Don't auto-select during exchange phase
    if (isExchangePhase) return;
    // In test mode, always allow auto-select; in multiplayer, check if it's our turn
    const isMyTurn = isTestMode || (gameState?.currentPlayer === activePlayerIndex);
    if (validPlays.length === 1 && isMyTurn) {
      setSelectedCards(validPlays[0]);
    }
  }, [validPlays, isTestMode, activePlayerIndex, gameState?.currentPlayer, isExchangePhase]);

  // Sort cards helper - returns sorted copy of hand
  const sortCards = useCallback((cards, mode) => {
    if (!cards) return [];
    const cardsCopy = [...cards];

    // Suit order: Karo (♦) → Herz (♥) → Pik (♠) → Kreuz (♣)
    const suitOrder = { '♦': 0, '♥': 1, '♠': 2, '♣': 3 };

    if (mode === 'suit') {
      // Sort by suit first, then by value within suit
      return cardsCopy.sort((a, b) => {
        const suitDiff = (suitOrder[a.suit] ?? 99) - (suitOrder[b.suit] ?? 99);
        if (suitDiff !== 0) return suitDiff;
        return a.value - b.value;
      });
    } else {
      // Default: sort by value first, then by suit
      return cardsCopy.sort((a, b) => {
        if (a.value !== b.value) return a.value - b.value;
        return (suitOrder[a.suit] ?? 99) - (suitOrder[b.suit] ?? 99);
      });
    }
  }, []);

  // Sorted hand - recalculates when sortMode or currentHand changes
  const sortedHand = useMemo(() => {
    return sortCards(currentHand, sortMode);
  }, [currentHand, sortMode, sortCards]);

  // Toggle sort mode
  const toggleSortMode = useCallback(() => {
    setSortMode(prev => prev === 'value' ? 'suit' : 'value');
  }, []);

  // Card selection toggle
  const toggleCardSelection = useCallback((card) => {
    // During exchange, always allow selection (HandCards handles locked cards)
    // In normal play (not test mode), only allow selection on your turn
    if (!isExchangePhase && !isTestMode) {
      if (gameState?.currentPlayer !== activePlayerIndex) {
        return;
      }
    }

    setSelectedCards(prev => {
      const isSelected = prev.some(c => c.rank === card.rank && c.suit === card.suit);
      soundService.play(isSelected ? 'card_deselect' : 'card_select');
      if (isSelected) {
        return prev.filter(c => !(c.rank === card.rank && c.suit === card.suit));
      }
      return [...prev, card];
    });
  }, [isTestMode, activePlayerIndex, gameState?.currentPlayer, isExchangePhase]);

  // Get card playability status (uses cached validPlays)
  const getCardPlayabilityStatus = useCallback((card) => {
    if (!gameState || !hasValidPlays) return 'unplayable';

    // In multiplayer, only show playable status when it's my turn
    if (!isTestMode && !isExchangePhase) {
      if (gameState?.currentPlayer !== activePlayerIndex) {
        return 'unplayable';
      }
    }

    const isSelected = selectedCards.some(c => c.rank === card.rank && c.suit === card.suit);
    if (isSelected) return 'selected';

    // No selection yet - show which cards can be part of any valid play
    if (selectedCards.length === 0) {
      const isPlayable = validPlays.some(play =>
        play.some(playCard => playCard.rank === card.rank && playCard.suit === card.suit)
      );
      return isPlayable ? 'playable' : 'unplayable';
    }

    // Cards selected - check if this card can be combined
    const testSelection = [...selectedCards, card];
    const sortedTest = [...testSelection].sort((a, b) => a.value - b.value || suitValues[a.suit] - suitValues[b.suit]);

    // Check if adding this card would still be part of a valid play
    const canCombine = validPlays.some(play =>
      play.length >= sortedTest.length &&
      sortedTest.every(testCard =>
        play.some(playCard => playCard.rank === testCard.rank && playCard.suit === testCard.suit)
      )
    );
    return canCombine ? 'combinable' : 'unplayable';
  }, [gameState, hasValidPlays, selectedCards, validPlays, isTestMode, isExchangePhase, activePlayerIndex]);

  // Check if current selection is a valid play (uses cached validPlays)
  const isSelectionValid = useCallback(() => {
    if (!gameState || selectedCards.length === 0) return false;

    const sortedSelection = [...selectedCards].sort((a, b) =>
      a.value - b.value || suitValues[a.suit] - suitValues[b.suit]
    );

    return validPlays.some(play =>
      play.length === sortedSelection.length &&
      play.every((c, i) => c.rank === sortedSelection[i].rank && c.suit === sortedSelection[i].suit)
    );
  }, [gameState, selectedCards, validPlays]);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedCards([]);
  }, []);

  return {
    selectedCards,
    setSelectedCards,
    sortMode,
    sortedHand,
    currentHand,
    hasValidPlays,
    toggleCardSelection,
    toggleSortMode,
    getCardPlayabilityStatus,
    isSelectionValid,
    clearSelection,
  };
}
